/**
 * Ketenregeling (art. 7:668a BW) — één telling voor API en browser.
 *
 * Opvolgende contracten voor bepaalde tijd met tussenpozen van zes maanden of
 * minder vormen een keten. Meer dan drie contracten, of samen meer dan 36
 * maanden, en het volgende contract is van rechtswege voor onbepaalde tijd.
 * De API weigert dan een nieuw bepaald-contract (409 KETEN_ONBEPAALD), de
 * beslis-kaart biedt alleen "verlengen voor onbepaalde tijd".
 *
 * Regels van de telling:
 * - Alleen contracten die in werking zijn (geweest) tellen: getekend,
 *   verstuurd, werkgever_getekend, verlopen, beëindigd (archiefrijen). Een
 *   concept telt niet. Een stilzwijgende verlenging (getekend, vlag) telt als
 *   eigen schakel.
 * - Een contract dat begint vóór het einde van de vorige schakel is geen
 *   opvolger maar een vervanging (of een soft-delete-dubbel, beëindigd doet
 *   dubbel dienst): het wordt in die schakel opgenomen, niet apart geteld.
 * - Een tussenpoos van meer dan zes maanden breekt de keten.
 * - Een contract voor onbepaalde tijd sluit de keten af: wat ervoor lag telt
 *   niet meer mee.
 * - Cao-afwijkingen zijn niet verwerkt (zie runbook contract-einde.md).
 *
 * ═══ BRON VAN WAARHEID ═══
 * Byte-identieke spiegels (Vercel-functions importeren niet buiten hun
 * app-root). Wijzig je iets, wijzig dan BEIDE en draai
 * scripts/smoke-workforce.sh (hash-pariteitscheck):
 *   1. brands/cinco/apps/office-app/api/_lib/contract-keten.ts
 *   2. brands/cinco/apps/office-app/src/lib/contract-keten.ts
 */

export interface KetenContract {
  contract_number?: string | null;
  contract_type: string;
  status: string;
  start_date: string;
  end_date: string | null;
  stilzwijgend_verlengd?: boolean | null;
}

export interface KetenStand {
  /** aantal schakels (contracten voor bepaalde tijd) in de lopende keten */
  aantal: number;
  eersteStart: string | null;
  laatsteEinde: string | null;
  /** hele maanden van eersteStart t/m laatsteEinde (of het voorgenomen einde) */
  maandenTotaal: number;
  /** het volgende contract is van rechtswege voor onbepaalde tijd */
  volgendeMoetOnbepaald: boolean;
  reden: string | null;
  /** contractnummers per schakel, oudste eerst */
  schakels: string[];
}

const TELT_MEE = new Set(['getekend', 'verstuurd', 'werkgever_getekend', 'verlopen', 'beëindigd']);
const MAX_CONTRACTEN = 3;
const MAX_MAANDEN = 36;
const MAX_TUSSENPOOS_MAANDEN = 6;

function parse(iso: string): Date {
  return new Date(iso + 'T00:00:00Z');
}

function fmt(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function plusMaanden(iso: string, maanden: number): string {
  const d = parse(iso);
  const dag = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + maanden);
  const laatste = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(dag, laatste));
  return fmt(d);
}

function plusDagen(iso: string, dagen: number): string {
  const d = parse(iso);
  d.setUTCDate(d.getUTCDate() + dagen);
  return fmt(d);
}

/** Hele maanden van `van` t/m `tot` (inclusief), 2025-04-21 t/m 2026-04-20 = 12. */
function heleMaanden(van: string, tot: string): number {
  const eind = plusDagen(tot, 1);
  let n = 0;
  while (plusMaanden(van, n + 1) <= eind) n++;
  return n;
}

/** De periode van `van` t/m `tot` is langer dan `maanden` maanden. */
function langerDan(van: string, tot: string, maanden: number): boolean {
  return plusDagen(tot, 1) > plusMaanden(van, maanden);
}

/**
 * Stand van de keten op `peildatum`. Met `voorgenomenEinde` toetst de
 * 36-maandengrens of een opvolger tot die datum nog bepaald mag zijn.
 */
export function ketenStand(
  contracten: KetenContract[],
  peildatum: string,
  voorgenomenEinde?: string | null,
): KetenStand {
  const relevant = contracten
    .filter(c => TELT_MEE.has(c.status) && c.start_date <= peildatum)
    .sort((a, b) => a.start_date.localeCompare(b.start_date));

  let schakels: { start: string; einde: string | null; nummer: string }[] = [];
  for (const c of relevant) {
    const nummer = c.contract_number ?? '?';
    if (c.contract_type !== 'bepaald') {
      // Onbepaald sluit de keten af; wat daarna komt begint opnieuw.
      schakels = [];
      continue;
    }
    const vorige = schakels[schakels.length - 1];
    if (vorige && (vorige.einde == null || c.start_date <= vorige.einde)) {
      // Overlap: vervanging of dubbel, hoort bij dezelfde schakel.
      if (vorige.einde != null && (c.end_date == null || c.end_date > vorige.einde)) vorige.einde = c.end_date;
      continue;
    }
    if (vorige && vorige.einde != null && c.start_date > plusMaanden(vorige.einde, MAX_TUSSENPOOS_MAANDEN)) {
      schakels = [];
    }
    schakels.push({ start: c.start_date, einde: c.end_date, nummer });
  }

  const aantal = schakels.length;
  const eersteStart = schakels[0]?.start ?? null;
  const laatsteEinde = schakels[aantal - 1]?.einde ?? null;
  const eindVoorToets = voorgenomenEinde ?? laatsteEinde;
  const maandenTotaal = eersteStart && eindVoorToets ? heleMaanden(eersteStart, eindVoorToets) : 0;

  let reden: string | null = null;
  if (aantal >= MAX_CONTRACTEN) {
    reden = `${aantal} contracten voor bepaalde tijd in de keten; een ${aantal + 1}e is van rechtswege voor onbepaalde tijd`;
  } else if (eersteStart && eindVoorToets && langerDan(eersteStart, eindVoorToets, MAX_MAANDEN)) {
    reden = `keten loopt sinds ${eersteStart} en komt ${voorgenomenEinde ? 'met de opvolger ' : ''}boven 36 maanden; het volgende contract is van rechtswege voor onbepaalde tijd`;
  }

  return {
    aantal,
    eersteStart,
    laatsteEinde,
    maandenTotaal,
    volgendeMoetOnbepaald: reden !== null,
    reden,
    schakels: schakels.map(s => s.nummer),
  };
}
